<template>
  <v-card>
    <v-card-title class="i-dialog-header-div">Upload draft images</v-card-title>
    <v-card-text class="py-4">
      <ul class="mb-4">
        <li>Choose a single image, multiple images or a ZIP file.</li>
        <li>
          Name each image with its supplier item code (for example, ABC123.jpg).
        </li>
        <li>
          Matching images replace the main image on your draft products,
          including drafts outside the current page or filters.
        </li>
        <li>JPG, JPEG, PNG, WEBP and AVIF only, up to 1 MB per image.</li>
        <li>Maximum 15 MB per selected file and 16 MB per batch.</li>
        <li>
          ZIP files may contain up to 1,000 entries and 16 MB of expanded data.
        </li>
        <li>
          Unmatched files, unsupported files and duplicate matches are skipped.
        </li>
      </ul>
      <input
        id="draft-image-files"
        ref="fileUploader"
        aria-label="Select images or ZIP files"
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.webp,.avif,.zip"
        style="display: none"
        :disabled="uploading"
        @change="selectFiles"
      />
      <v-card class="p-action-card my-4" :class="{ 'is-disabled': uploading }">
        <label
          for="draft-image-files"
          role="button"
          :tabindex="uploading ? -1 : 0"
          :aria-disabled="uploading"
          @keydown.enter.prevent="$refs.fileUploader.click()"
          @keydown.space.prevent="$refs.fileUploader.click()"
        >
          <div class="p-text-overlay">
            <v-icon class="p-icon mb-2">mdi-cloud-upload-outline</v-icon>
            <div>
              {{
                files.length
                  ? 'Click here to change selected files'
                  : 'Click here to choose images or a ZIP file'
              }}
            </div>
          </div>
        </label>
      </v-card>
      <p v-if="files.length" class="mt-3">
        {{ files.length }} file(s) selected
      </p>
      <p v-if="error" role="alert" class="mt-3">{{ error }}</p>
      <div v-if="uploading" role="status" aria-live="polite">
        <v-progress-linear
          :model-value="progress"
          :indeterminate="progress === 100"
        />
        {{
          progress < 100
            ? `Sending files: ${progress}%`
            : 'Matching and uploading images…'
        }}
      </div>
      <div v-if="results.length" role="status" aria-live="polite" class="mt-4">
        <p>{{ summary }}</p>
        <ul class="results">
          <li
            v-for="(result, index) in results"
            :key="index"
            :class="{
              'size-error':
                result.status === 'SKIPPED' &&
                result.reason === 'Image exceeds 1 MB'
            }"
          >
            <strong>{{ result.filename }}</strong
            >: {{ result.status.toLowerCase() }} — {{ result.reason }}
          </li>
        </ul>
      </div>
    </v-card-text>
    <v-divider />
    <v-card-actions class="i-dialog-actions-div justify-center">
      <v-btn rounded :disabled="uploading" @click="$emit('close')">Close</v-btn>
      <v-btn
        rounded
        class="i-btn-block-secondary"
        :loading="uploading"
        :disabled="!files.length || uploading || !!error || results.length > 0"
        @click="upload"
        >Upload</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import { uploadDraftImages } from '@/services/products'

export default {
  emits: ['close', 'completed'],
  data: () => ({
    files: [],
    results: [],
    error: '',
    uploading: false,
    progress: 0
  }),
  computed: {
    summary() {
      const count = (status) =>
        this.results.filter((result) => result.status === status).length
      return `${count('UPLOADED')} uploaded, ${count('SKIPPED')} skipped, ${count('FAILED')} failed.`
    }
  },
  methods: {
    selectFiles(event) {
      this.files = Array.from(event.target.files || [])
      this.results = []
      this.error = ''
      if (this.files.some((file) => file.size > 15 * 1024 * 1024)) {
        this.error = 'Each selected file must be 15 MB or smaller.'
      } else if (
        this.files.reduce((size, file) => size + file.size, 0) >
        16 * 1024 * 1024 - 64 * 1024
      ) {
        this.error = 'Select a smaller batch to fit the 16 MB request limit.'
      }
    },
    async upload() {
      if (this.uploading || !this.files.length || this.error) return
      this.uploading = true
      this.progress = 0
      try {
        this.results = await uploadDraftImages(this.files, (event) => {
          if (event.total)
            this.progress = Math.round((event.loaded / event.total) * 100)
        })
        this.$emit('completed')
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          'Upload did not complete. Refresh drafts before retrying; some images may have uploaded.'
        this.$emit('completed')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.p-action-card {
  background-color: #f8f9fa;
  border-radius: 10px;
  border: 2px dashed $secondary-color;
  padding: 16px 24px;
  box-shadow: unset;

  label {
    display: block;
    cursor: pointer;
  }

  &:hover:not(.is-disabled) {
    background-color: var(--v-lightHighlightBackground-lighten1);
    transition: background-color 0.3s ease-in-out;
  }

  &.is-disabled label {
    cursor: default;
  }
}

.p-icon {
  color: $secondary-color;
  height: 58px;
  width: 58px;
  font-size: 58px;
}

.p-text-overlay {
  font-weight: normal;
  text-align: center;
  font-size: 14px;
}

.size-error {
  color: $red-color;
}

.results {
  max-height: 280px;
  overflow: auto;
  overflow-wrap: anywhere;
}
</style>
